import { engineComponent, StringUtils } from "neo-common";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ILayoutElement } from "./ILayoutElement";
import { LayoutManager } from "../Managers/LayoutManager";
import { ShapeElement } from "../Components/Elements/Graphics/ShapeElement";
import { StyleManager } from "../Components/Managers/StyleManager";
import { SwitchStyle } from "../Components/Elements/Styles/SwitchStyle";
import { Tweens } from "../Components/Effects/Tweens";
import { FrameTimerEventEmitter } from "../Utils/FrameTimer";
import { StageController } from "../Components/StageController";

@engineComponent(ComponentTypes.DisplayObject)
export class LayoutSwitchElement extends SwitchElement implements ILayoutElement {
    private baseStyle: SwitchStyle;

    private portraitStyle: SwitchStyle;

    private inPortraitMode: boolean;

    private tweenProgress: Object;

    private tempTimer: FrameTimerEventEmitter;

    private tempNextCase: string;

    private tempTimerData: string;

    private size: string;

    private sizeWidth: number;

    private sizeHeight: number;

    constructor() {
        super();

        this.inPortraitMode = false;
        LayoutManager.addLayoutElement(this);
    }

    createByNode(node: Element) {
        super.createByNode(node);

        if (node.hasAttribute(LayoutSwitchElementAttributes.PORTRAIT_STYLE)) {
            this.portraitStyle = <SwitchStyle>(
                StyleManager.Styles[node.getAttribute(LayoutSwitchElementAttributes.PORTRAIT_STYLE)]
            );
        }

        if (this.style != null) {
            this.baseStyle = this.style;
        }
    }

    updateByNode(node: Element) {
        super.updateByNode(node);

        if (node.hasAttribute("Width")) {
            this.sizeWidth = parseInt(node.getAttribute("Width"));
            this.updateSize();
        }

        if (node.hasAttribute("Height")) {
            this.sizeHeight = parseInt(node.getAttribute("Height"));
            this.updateSize();
        }

        if (node.hasAttribute("Size")) {
            this.size = node.getAttribute("Size");
            this.updateSize();
        }
    }

    protected updateSize(): void {
        if (this.size != null) {
            switch (this.size) {
                case "cover":
                    this.calculateCoverScale();
                    break;
            }
        }
    }

    protected calculateCoverScale(): void {
        if (this.sizeWidth > 0 && this.sizeHeight > 0) {
            let screenWidth = StageController.Renderer.screen.width;
            let screenHeight = StageController.Renderer.screen.height;

            let screenWRatio = screenWidth / this.sizeWidth;
            let screenHRatio = screenHeight / this.sizeHeight;

            if (screenWRatio > screenHRatio) {
                this.scale.set(screenWRatio);
            } else {
                this.scale.set(screenHRatio);
            }
        }
    }

    onLayoutChange(isPortrait: boolean): void {
        this.updateSize();

        //update layout only if orientation was changed
        if (isPortrait == this.inPortraitMode) {
            return;
        }

        let currentCase = this.clearCaseLayoutPrefix(this.getCurrentCase());
        let style = isPortrait ? this.portraitStyle : this.baseStyle;

        if (style != null) {
            let caseType = LayoutManager.getSwitchPrefix() + currentCase;
            this.case = "";
            this.style = style;
            this.storeData(caseType);
            this.goToCase(caseType);
            this.restoreData(caseType);

            this.inPortraitMode = isPortrait;
        }
    }

    private clearCaseLayoutPrefix(caseType: string): string {
        if (!StringUtils.isNullOrEmpty(LayoutManager.getSwitchPrefix())) {
            caseType = caseType.replace(LayoutManager.getPreviousSwitchPrefix(), "");
        }

        return caseType;
    }

    private storeData(caseType: string): void {
        //store tween progress

        // TweenMax.pauseAll(); //ToDo needed ?
        this.tweenProgress = {};

        let node = this.style.cases[caseType];
        if (node != null) {
            let next: Element = node.firstElementChild;

            while (next != null) {
                let elem = this.elements.childs[next.nodeName];

                if (elem != null) {
                    let tweensArray = Tweens.getRegisteredTweens(elem.uid);

                    if (tweensArray != null && tweensArray.length > 0) {
                        this.tweenProgress[elem.uid] = [];
                        for (let i = 0; i < tweensArray.length; i++) {
                            this.tweenProgress[elem.uid].push(tweensArray["tweens"][i].progress());
                        }
                    }
                }

                next = next.nextElementSibling;
            }
        }

        //store timer
        this.tempTimer = this.timer;
        this.timer = null;

        //store nextCase
        if (this.style.cases[caseType].hasAttribute("NextCase")) {
            this.tempNextCase = this.style.cases[caseType].getAttribute("NextCase");
            this.style.cases[caseType].removeAttribute("NextCase");
        }

        //store timerData
        if (this.style.cases[caseType].hasAttribute("Timer")) {
            this.tempTimerData = this.style.cases[caseType].getAttribute("Timer");
            this.style.cases[caseType].removeAttribute("Timer");
        }
    }

    private restoreData(caseType: string): void {
        //restore tween progress

        // TweenMax.pauseAll(); //ToDo needed ?
        if (this.tweenProgress != null) {
            for (let key in this.tweenProgress) {
                let tweensArray = Tweens.getRegisteredTweens(key);

                if (tweensArray != null && tweensArray.length > 0) {
                    for (let i = 0; i < tweensArray.length; i++) {
                        tweensArray["tweens"][i].progress(this.tweenProgress[key][i]);
                    }
                }
            }

            this.tweenProgress = null;
        }

        //restore timer
        this.timer = this.tempTimer;
        this.tempTimer = null;

        //restore nextCase
        if (this.tempNextCase != null) {
            this.style.cases[caseType].setAttribute("NextCase", this.tempNextCase);
            this.tempNextCase = null;
        }

        //restore nodeTimer
        if (this.tempTimerData != null) {
            this.style.cases[caseType].setAttribute("Timer", this.tempTimerData);
            this.tempTimerData = null;
        }
    }

    clone(): LayoutSwitchElement {
        const element: LayoutSwitchElement = new LayoutSwitchElement();

        element.style = this.style;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;
        element.name = this.name;

        element.elements = this.elements.clone();
        element.addChild(element.elements);

        element.case = this.case;
        element.baseStyle = this.baseStyle;
        element.portraitStyle = this.portraitStyle;
        element.inPortraitMode = this.inPortraitMode;

        element.size = this.size;
        element.sizeWidth = this.sizeWidth;
        element.sizeHeight = this.sizeHeight;

        if (this.maskSprite != null) {
            let elementMask = new PIXI.Sprite((<PIXI.Sprite>this.maskSprite).texture);
            elementMask.position.x = this.maskSprite.position.x;
            elementMask.position.y = this.maskSprite.position.y;

            element.maskSprite = elementMask;
            element.addChild(elementMask);
            element.mask = elementMask;
        }

        element.markAsCloned(); // must be called last before return!
        return element;
    }
}

const LayoutSwitchElementAttributes = {
    PORTRAIT_STYLE: "PortraitStyle",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/LayoutSwitchElement.ts
 **/